/**
 * **事件节点包** —— "什么能让图跑起来"。
 *
 * ## 它为什么是一个包，而不是内核的一部分
 *
 * 内核提供的是**运行通道**（`node/runGraph.ts`：范围 / 顺序 / 取消 / 串行），
 * 而"有哪些事件"是**领域知识**：手动按钮、打开时、定时器、webhook、
 * 文件变化……它们的数量与语义会一直长。
 * 所以事件做成**域节点包**（用户定的方向：有语义的节点交给核心与域包）。
 *
 * ## 三种节点在这套结构里的位置
 *
 * ```
 *   事件节点（无输入、有输出）        ← 触发源：它让图跑起来
 *     ↓
 *   纯节点（设计期就在算）            ← 计算
 *     ↓
 *   副作用节点（只有运行期才跑）        ← 干活（下一批：tool.http）
 * ```
 *
 * ⚠️ 事件节点**在设计期产出空值**（`{}` = "算过了，但没有值"），
 * 于是下游纯节点回退到自己的内联默认值 —— 这与 `muted` / 编辑器专用节点
 * 是同一条规矩。**绝不在设计期偷偷触发一次** ✗：那会让"打开文档"
 * 变成"执行文档"（`docs/js-node.md` §2）。
 *
 * ## ⚠️ 现在只有浏览器宿主支持的那几种
 *
 * `manual`（按钮）与 `startup`（打开时）在浏览器里就是完整的 ——
 * 不需要常驻进程 ✓。定时器（`timer`）、固定时刻（`schedule`）、
 * 入站请求（`webhook`）以后加：它们的**限制**要写在工具卡片上
 * （见 `types/capability.ts` 的 `BROWSER_HOST.limits`）。
 */
import type { NodePackage, NodeTypeDef } from '../../node/package'
import type { NodeEvaluator } from '../../node/evaluate'
import type { NodeRunner } from '../../node/runGraph'

/**
 * 设计期的取值：**空对象**。
 *
 * ⚠️ 不是 `undefined`：空对象表示"这个节点算过了，但没有值"，
 * 下游因此回退到内联默认值（与 `muted` 同一条契约）。
 * 返回 `undefined` 会被当成"没算出来"，下游的诊断会多一条"未注册求值器"
 * 那类噪音。
 */
const notTriggered: NodeEvaluator = () => ({})

/** 事件负载里那个"什么时候发生的"—— 输出给下游做时间戳用 */
function stampOf(at: number): string {
  const date = new Date(at)
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
}

/**
 * **手动**：用户点了按钮（或按了运行键）。
 *
 * 判据（用户原话）："点击开始按钮……当事件被编辑器触发的时候节点就会开始运行"。
 */
const manualRunner: NodeRunner = async () => {
  const at = Date.now()
  return {
    at,
    out: `手动触发 · ${stampOf(at)}`,
  }
}

/** **打开时**：这份工具被打开时跑一次 */
const startupRunner: NodeRunner = async () => {
  const at = Date.now()
  return {
    at,
    out: `打开时触发 · ${stampOf(at)}`,
  }
}

/**
 * 事件节点共用的行：一个名字 + 两个输出（什么时候 + 一句话）。
 *
 * ⚠️ 名字是**给用户看的**（"点我开始"），不影响任何逻辑 ——
 * 端口的身份是 id（`at` / `out`），运行端不认名字（同变参行的规矩）。
 * ⚠️ 那句"这个节点是干嘛的"放在 `hint` 上（标题旁 ⓘ 的悬停提示），
 * **不**在行里 —— 行上的 `display` 是"渲染某个值"的坑位（`render: 'debug'` 那种），
 * 不是写死一行说明用的（我第一版就写错了：`text` 字段根本不存在，会被静默忽略）。
 */
function eventRows(label: string, placeholder: string) {
  return [
    {
      kind: 'property' as const,
      id: 'label',
      label: '名称',
      widget: { id: 'label', kind: 'text' as const, value: label, placeholder },
    },
    { kind: 'output' as const, id: 'at', label: '时刻', socket: { id: 'at', type: 'datetime' as const } },
    { kind: 'output' as const, id: 'out', label: '说明', socket: { id: 'out', type: 'text' as const } },
  ]
}

const MANUAL_TYPE: NodeTypeDef = {
  type: 'event.manual',
  /* 播放三角：手动触发的经典标记 */
  icon: '<path d="M8 5.5l11 6.5-11 6.5z"/>',
  title: '事件 · 手动触发',
  category: 'event',
  width: 208,
  keywords: ['event', 'manual', 'button', 'click', '事件', '手动', '按钮', '触发'],
  hint: '点一下按钮就让这条链跑起来（工具箱里的"开始"）',
  /* ★ 触发器声明在节点类型上：文档用了哪些触发方式，由摆了哪些节点决定 */
  trigger: 'manual',
  rows: eventRows('运行', '例如：点我开始'),
}

const STARTUP_TYPE: NodeTypeDef = {
  type: 'event.startup',
  /* 上电符号：打开文档就跑 */
  icon: '<path d="M12 3.5v8"/><path d="M6.8 6.8a7.5 7.5 0 1 0 10.4 0"/>',
  title: '事件 · 打开时',
  category: 'event',
  width: 208,
  keywords: ['event', 'startup', 'open', 'load', '事件', '打开', '启动'],
  hint: '这份工具被打开时跑一次（浏览器里就是"切到这个工具"）',
  trigger: 'startup',
  rows: eventRows('打开时', '例如：打开就刷新'),
}

export const EVENT_PACKAGE: NodePackage = {
  id: 'event',
  label: '事件',
  /*
   * ⚠️ `domain`：可以卸载（卸了之后文档照常打开，事件节点只是算不出值、
   * 也跑不起来 —— 界面上会显示"缺包"而不是崩）。
   * 它不是 `base`：基础能力（常量 / 工具节点）才不可卸载。
   */
  kind: 'domain',
  /*
   * ⚠️ 图标：分类一个 + 两个事件类型各一个（见 `@/node/nodeIcons` 的画法说明）。
   * 事件是**触发器** —— 一眼看出"这是个起点"比看出"它是哪种起点"更要紧，
   * 所以两个类型用的是同一族的两种标记（播放 / 上电）。
   */
  categories: [
    {
      id: 'event',
      label: '事件',
      icon: '<path d="M13.5 3L6.5 13.5H12l-1.5 7.5 7-11H12z"/>',
    },
  ],
  nodeTypes: [MANUAL_TYPE, STARTUP_TYPE],
  evaluators: {
    'event.manual': notTriggered,
    'event.startup': notTriggered,
  },
  runners: {
    'event.manual': manualRunner,
    'event.startup': startupRunner,
  },
}
